export type SourceKind =
  | "leitlinie"
  | "behoerde"
  | "fachinfo"
  | "monitoring"
  | "sekundaerliteratur"
  | "primaerliteratur";

export interface Source {
  id: string;
  title: string;
  publisher: string;
  year: string;
  kind: SourceKind;
  url: string;
  backs_de: string;
  backs_en: string;
  caveat_de?: string;
  caveat_en?: string;
}

export interface PageSources {
  intro_de: string;
  intro_en: string;
  sourceIds: string[];
  openClaims_de: string[];
  openClaims_en: string[];
}

export const SOURCES: Record<string, Source> = {
  "dailymed-fentanyl-system-2026": {
    id: "dailymed-fentanyl-system-2026",
    title: "Fentanyl System (Fentanyl-Pflaster), US Prescribing Information",
    publisher: "ALVOGEN, DailyMed (U.S. National Library of Medicine)",
    year: "03/2026",
    kind: "fachinfo",
    url: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=242759ef-cb6d-4e3e-9f8d-5e31efa1f289",
    backs_de: "Hautdepot und langsamer Serumspiegelabfall nach Pflasterentfernung: Abschnitte 10 und 12.3. Erhöhte Aufnahme durch äußere Wärme und bei Fieber: Abschnitte 5.8 und 5.9.",
    backs_en: "Skin depot and slow serum concentration decline after patch removal: sections 10 and 12.3. Increased absorption with external heat and fever: sections 5.8 and 5.9.",
    caveat_de: "US-Fachinformation für ein transdermales Präparat; keine Übertragung der Zeitangabe auf andere Applikationswege. Quellenprüfung: 07.10.2026.",
    caveat_en: "US prescribing information for a transdermal product; the time estimate does not apply to other routes. Source checked: 2026-10-07.",
  },
  "dailymed-methadose-2026": {
    id: "dailymed-methadose-2026",
    title: "Methadose (Methadonhydrochlorid, orales Konzentrat), US Prescribing Information",
    publisher: "SpecGx LLC, DailyMed (U.S. National Library of Medicine)",
    year: "04/2026",
    kind: "fachinfo",
    url: "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=808a9d0b-720b-4034-a862-5122ff514608",
    backs_de: "QT-Verlängerung und Torsades de pointes, einschließlich Risikofaktoren und Überwachung: hervorgehobener Warnhinweis (Boxed Warning) und Abschnitt 5.3.",
    backs_en: "QT prolongation and torsades de pointes, including risk factors and monitoring: Boxed Warning and section 5.3.",
    caveat_de: "US-Fachinformation für orales Methadon. Fälle sind nicht auf hohe Dosen beschränkt. Quellenprüfung: 07.10.2026.",
    caveat_en: "US prescribing information for oral methadone. Cases are not limited to high doses. Source checked: 2026-10-07.",
  },
  "cdc-opioid-2022": {
    id: "cdc-opioid-2022",
    title: "CDC Clinical Practice Guideline for Prescribing Opioids for Pain, United States, 2022",
    publisher: "Centers for Disease Control and Prevention, MMWR Recommendations and Reports 71(RR-3)",
    year: "2022",
    kind: "leitlinie",
    url: "https://www.cdc.gov/mmwr/volumes/71/rr/rr7103a1.htm",
    backs_de:
      "Morphin-Äquivalenzfaktoren (MME), die nicht-lineare Methadon-Umrechnung und die Warnung vor gleichzeitiger Verordnung von Opioiden und Benzodiazepinen.",
    backs_en:
      "Morphine milligram equivalent conversion factors, the non-linear methadone conversion, and the warning against concurrent opioid and benzodiazepine prescribing.",
  },
  "awmf-lonts": {
    id: "awmf-lonts",
    title: "S3-Leitlinie Langzeitanwendung von Opioiden bei chronischen nicht-tumorbedingten Schmerzen (LONTS)",
    publisher: "AWMF, Registernummer 145-003, Deutsche Schmerzgesellschaft",
    year: "145-003",
    kind: "leitlinie",
    url: "https://register.awmf.org/de/leitlinien/detail/145-003",
    backs_de:
      "Deutschsprachige Umrechnungsfaktoren zwischen Opioiden sowie Indikation und Grenzen einer Opioidtherapie.",
    backs_en: "German-language opioid conversion factors plus the indications for and limits of opioid therapy.",
  },
  "awmf-lonts-4-2026-kf": {
    id: "awmf-lonts-4-2026-kf",
    title: "S3-Leitlinie LONTS, Langversion 4.0 (Konsultationsfassung)",
    publisher: "Deutsche Schmerzgesellschaft, AWMF 145-003 (Konsultation: 145-003KF)",
    year: "September 2026",
    kind: "leitlinie",
    url: "https://register.awmf.org/assets/guidelines/145-003l_S3_KF_Langzeitanwendung-Opioide-chronische-nicht-tumorbedingte-Schmerzen-LONTS_2026-09.pdf#page=62",
    backs_de: "Serotonerge Opioid-Wechselwirkungen: Kapitel 6.2.9, Abschnitt Serotonin-Syndrom, S. 57–58 (PDF-Seiten 62–63).",
    backs_en: "Serotonergic opioid interactions: section 6.2.9, Serotonin-Syndrom, pp. 57–58 (PDF pages 62–63).",
    caveat_de: "Konsultationsfassung, noch nicht endgültig. Quellenprüfung: 07.10.2026. Keine quantitative Bewertung einzelner Matrix-Paare.",
    caveat_en: "Consultation draft, not final. Source checked: 2026-10-07. No quantitative assessment of individual matrix pairs.",
  },
  "awmf-medikamentenbezogene-stoerungen": {
    id: "awmf-medikamentenbezogene-stoerungen",
    title: "S3-Leitlinie Medikamentenbezogene Störungen",
    publisher: "AWMF, Registernummer 038-025",
    year: "038-025",
    kind: "leitlinie",
    url: "https://register.awmf.org/de/leitlinien/detail/038-025",
    backs_de:
      "Benzodiazepin-Abhängigkeit, Entzugssyndrom und die Empfehlung, ausschließlich ärztlich begleitet auszuschleichen.",
    backs_en:
      "Benzodiazepine dependence, withdrawal syndrome, and the recommendation to taper only under medical supervision.",
    caveat_de:
      "Im AWMF-Register inzwischen als abgelaufen dokumentiert. Die Sicherheitsgrundsätze werden durch die neuere ASAM-Leitlinie von 2025 gestützt.",
    caveat_en:
      "Now documented as expired in the AWMF register. Its safety principles are supported by the newer ASAM guideline from 2025.",
  },
  "ashton-manual": {
    id: "ashton-manual",
    title: "Benzodiazepines: How They Work and How to Withdraw (Ashton Manual)",
    publisher: "C. Heather Ashton, Newcastle University, benzo.org.uk",
    year: "2002, revidiert 2007",
    kind: "sekundaerliteratur",
    url: "https://www.benzo.org.uk/manual/",
    backs_de: "Die Diazepam-Äquivalenzwerte und die Entzugsschemata in der Benzo-Tabelle.",
    backs_en: "The diazepam equivalence values and the withdrawal schedules in the benzodiazepine table.",
    caveat_de:
      "Stand 2007. Prägend, aber in Teilen überholt. Neuere Äquivalenztabellen weichen im Einzelfall deutlich ab, etwa bei Lorazepam.",
    caveat_en:
      "Dated 2007. Influential but partly outdated. Newer equivalence tables differ noticeably in individual cases, for example lorazepam.",
  },
  "nhs-sps-benzo-equiv": {
    id: "nhs-sps-benzo-equiv",
    title: "Oral benzodiazepines and choosing equivalent doses",
    publisher: "NHS Specialist Pharmacy Service (SPS)",
    year: "Stand Juli 2025",
    kind: "behoerde",
    url: "https://www.sps.nhs.uk/articles/oral-benzodiazepines-and-choosing-equivalent-doses/",
    backs_de:
      "Äquivalenzdosen, die Halbwertszeit-Klassen kurz, intermediär und lang, und den Hinweis, dass Äquivalenzen grundsätzlich nicht exakt sind.",
    backs_en:
      "Equivalent doses, the short, intermediate and long half-life classes, and the point that equivalences are never exact.",
  },
  "fda-benzo-boxed-warning": {
    id: "fda-benzo-boxed-warning",
    title: "FDA requiring Boxed Warning updated to improve safe use of benzodiazepine drug class",
    publisher: "U.S. Food and Drug Administration, Drug Safety Communication",
    year: "2020",
    kind: "behoerde",
    url: "https://www.fda.gov/drugs/drug-safety-and-availability/fda-requiring-boxed-warning-updated-improve-safe-use-benzodiazepine-drug-class",
    backs_de:
      "Abhängigkeitspotenzial, körperliche Abhängigkeit und Entzugsreaktionen einschließlich Krampfanfällen bei abruptem Absetzen.",
    backs_en: "Abuse potential, physical dependence, and withdrawal reactions including seizures on abrupt discontinuation.",
  },
  "euda-edr-2026": {
    id: "euda-edr-2026",
    title: "European Drug Report 2026",
    publisher: "EUDA, Europäische Drogenagentur, früher EMCDDA",
    year: "2026",
    kind: "monitoring",
    url: "https://www.euda.europa.eu/publications/european-drug-report/2026_en",
    backs_de: "Europäische Lage zu Fentanyl, Nitazenen und Orphinen sowie gefälschten Arzneimitteln mit Nitazenen. Relevante Kapitel: The drug situation in Europe up to 2026; Drug-induced deaths; New psychoactive substances.",
    backs_en: "European developments involving fentanyl, nitazenes, orphines and fake medicines containing nitazenes. Relevant chapters: The drug situation in Europe up to 2026; Drug-induced deaths; New psychoactive substances.",
    caveat_de: "Berichtsjahr 2026, Todesfalldaten überwiegend 2024. Regional unterschiedliche Lage und begrenzte toxikologische Erfassung. Fentanylbezogene Todesfälle können auch abgezweigte Arzneimittel betreffen; kein Beleg für eine europaweite Dominanz illegal produzierten Fentanyls.",
    caveat_en: "Report published in 2026; mortality data mostly refer to 2024. Regional differences and limited toxicological detection. Fentanyl-related deaths may also involve diverted medicines; this does not establish Europe-wide dominance of illicitly produced fentanyl.",
  },
  "asam-benzo-tapering-2025": {
    id: "asam-benzo-tapering-2025",
    title: "Joint Clinical Practice Guideline on Benzodiazepine Tapering",
    publisher: "American Society of Addiction Medicine und American Academy of Addiction Psychiatry",
    year: "2025",
    kind: "leitlinie",
    url: "https://www.asam.org/quality-care/clinical-guidelines/benzodiazepine-tapering",
    backs_de:
      "Das individuell angepasste Ausschleichen unter klinischer Überwachung sowie die Äquivalenztabelle, die je nach Ursprungsquelle abweichende Werte ausweist, etwa Lorazepam 1 bis 2 mg oder Triazolam 0,25 bis 0,5 mg.",
    backs_en:
      "Individually adapted tapering under clinical supervision plus the dose equivalence table, which lists diverging values depending on the underlying source, for example lorazepam 1 to 2 mg or triazolam 0.25 to 0.5 mg.",
  },
  "nhs-scotland-benzo-zdrugs-2024": {
    id: "nhs-scotland-benzo-zdrugs-2024",
    title: "Quality Prescribing for Benzodiazepines and z-drugs: A Guide for Improvement 2024-2027",
    publisher: "Scottish Government, NHS Scotland",
    year: "2024",
    kind: "leitlinie",
    url: "https://www.gov.scot/publications/quality-prescribing-benzodiazepines-z-drugs-guide-improvement-2024-2027/",
    backs_de:
      "Die ungefähren Diazepam-Vergleichswerte für Z-Substanzen, etwa 10 mg Zolpidem oder 7,5 mg Zopiclon zu rund 5 mg Diazepam, und die Umstellung auf Diazepam vor einer Reduktion.",
    backs_en:
      "The approximate diazepam comparison values for z-drugs, for example 10 mg zolpidem or 7.5 mg zopiclone to roughly 5 mg diazepam, and the switch to diazepam before a reduction.",
  },
  "doble-zopiclone-1995": {
    id: "doble-zopiclone-1995",
    title: "The mechanism of action of zopiclone",
    publisher: "A. Döble, European Psychiatry 10(Suppl 3)",
    year: "1995",
    kind: "sekundaerliteratur",
    url: "https://www.sciencedirect.com/science/article/pii/0924933896800939",
    backs_de:
      "Dass Zopiclon nicht zwischen GABA-A-Rezeptoren mit unterschiedlichen alpha-Untereinheiten unterscheidet. Grundlage für die Korrektur, dass nicht alle Z-Substanzen alpha1-selektiv sind.",
    backs_en:
      "That zopiclone does not distinguish between GABA-A receptors with different alpha subunits. Basis for the correction that not all z-drugs are alpha1-selective.",
    caveat_de:
      "Ältere Publikation. Für Eszopiclon ist die Selektivität in neueren Arbeiten weiterhin unsicher.",
    caveat_en:
      "Older publication. For eszopiclone, newer work still leaves the selectivity uncertain.",
  },
  "an-flumazenil-2016": {
    id: "an-flumazenil-2016",
    title: "Flumazenil in benzodiazepine overdose",
    publisher: "H. An und S. Godwin, CMAJ 188(17-18)",
    year: "2016",
    kind: "sekundaerliteratur",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5135539/",
    backs_de:
      "Dass Flumazenil bei unklarer oder gemischter Überdosierung und bei bestehender Abhängigkeit kontraindiziert ist und Entzugskrampfanfälle auslösen kann.",
    backs_en:
      "That flumazenil is contraindicated in unknown or mixed overdose and in existing dependence, and can precipitate withdrawal seizures.",
  },
  "cdc-naloxone": {
    id: "cdc-naloxone",
    title: "Lifesaving Naloxone",
    publisher: "Centers for Disease Control and Prevention, Stop Overdose",
    year: "Stand 2024",
    kind: "behoerde",
    url: "https://www.cdc.gov/stop-overdose/caring/naloxone.html",
    backs_de:
      "Dass Naloxon eine Opioid-Überdosis umkehren kann und als Notfallmaßnahme sicher ist. Es hebt die Wirkung von Benzodiazepinen nicht auf.",
    backs_en:
      "That naloxone can reverse an opioid overdose and is safe as an emergency measure. It does not reverse the effect of benzodiazepines.",
  },
};

export const PAGE_SOURCES: Record<string, PageSources> = {
  "/benzo": {
    intro_de:
      "Die Tabelle stützt sich auf klinische Äquivalenz- und Leitlinienquellen. Äquivalenzen sind grobe Schätzwerte, keine Umrechnungsvorschrift. Die Wirkprofil-Punkte sind eine redaktionelle Einschätzung ohne validierte Messskala.",
    intro_en:
      "The table draws on clinical equivalence tables and guidelines. Equivalences are rough estimates, not a conversion rule. The effect profile dots are an editorial estimate without a validated measurement scale.",
    sourceIds: [
      "ashton-manual",
      "nhs-sps-benzo-equiv",
      "awmf-medikamentenbezogene-stoerungen",
      "fda-benzo-boxed-warning",
      "cdc-opioid-2022",
      "asam-benzo-tapering-2025",
      "nhs-scotland-benzo-zdrugs-2024",
      "doble-zopiclone-1995",
      "an-flumazenil-2016",
      "cdc-naloxone",
    ],
    openClaims_de: [
      "Dot-Skala 0 bis 5: redaktionelle ordinale Einschätzung ohne nachvollziehbare Herleitung, keine validierte Messskala und durch keine Einzelquelle belegt. Besonders das Abhängigkeitsrisiko lässt sich nicht als feste Stoffeigenschaft darstellen.",
      "Halbwertszeiten, aktive Metaboliten und typische Dosen je Substanz stammen aus den jeweiligen Fachinformationen. Diese sind noch nicht einzeln pro Wirkstoff hinterlegt, inklusive Indikation, Applikationsweg und Zulassungsregion.",
      "Eszopiclon und Zaleplon: für diese beiden Wirkstoffe ist in den hier genannten Standardtabellen keine belastbare Diazepam-Äquivalenz hinterlegt.",
      "Midazolam, orale Bioverfügbarkeit von etwa 40 Prozent: Beleg noch offen. Fachinformationen und Übersichtsarbeiten nennen ungefähr 36 bis 44 Prozent bei hoher Variabilität.",
      "Passionsblume, Dosisbereich 400 bis 800 mg: Beleg noch offen. Studien nutzten sehr unterschiedliche Extrakte, Extraktverhältnis und Standardisierung fehlen hier.",
    ],
    openClaims_en: [
      "Dot scale 0 to 5: editorial ordinal estimate without a traceable derivation, not a validated measurement scale and not backed by any single source. Dependence risk in particular cannot be presented as a fixed substance property.",
      "Half-lives, active metabolites and typical doses per substance come from the respective product information. These are not yet filed per active ingredient, including indication, route of administration and licensing region.",
      "Eszopiclone and zaleplon: no reliable diazepam equivalence is filed for these two substances in the standard tables named here.",
      "Midazolam, oral bioavailability of roughly 40 percent: source still open. Product information and reviews report roughly 36 to 44 percent with high variability.",
      "Passion flower, dose range 400 to 800 mg: source still open. Studies used very different extracts; extract ratio and standardisation are missing here.",
    ],
  },
  "/opioide": {
    intro_de:
      "Die Äquivalenzwerte folgen etablierten Umrechnungstabellen. Sie gelten für opioidgewöhnte Patientinnen und Patienten unter ärztlicher Kontrolle und sind keine Dosierungsanleitung.",
    intro_en:
      "The equivalence values follow established conversion tables. They apply to opioid-tolerant patients under medical supervision and are not dosing instructions.",
    sourceIds: ["cdc-opioid-2022", "awmf-lonts-4-2026-kf", "euda-edr-2026", "fda-benzo-boxed-warning", "dailymed-fentanyl-system-2026", "dailymed-methadose-2026"],
    openClaims_de: [
      "Dot-Skala 0 bis 5: redaktionelle ordinale Einschätzung, keine validierte Messskala.",
      "Einzelne Halbwertszeiten und typische Dosen je Substanz: Beleg noch offen.",
      "Heroin, Äquivalenz von etwa 3 bis 5 mg zu 10 mg Morphin: Beleg noch offen.",
    ],
    openClaims_en: [
      "Dot scale 0 to 5: editorial ordinal estimate, not a validated measurement scale.",
      "Individual half-lives and typical doses per substance: source still open.",
      "Heroin, equivalence of roughly 3 to 5 mg to 10 mg morphine: source still open.",
    ],
  },
  "/matrix": {
    intro_de:
      "Jede Substanzkombination führt ihre eigenen Quellen. Öffne eine Kombination und klappe im Detail-Panel den Abschnitt für Fachleute auf. Die folgenden Quellen gelten übergreifend.",
    intro_en:
      "Every substance combination carries its own sources. Open a combination and expand the expert section in the detail panel. The sources below apply across the whole matrix.",
    sourceIds: ["cdc-opioid-2022", "fda-benzo-boxed-warning", "euda-edr-2026"],
    openClaims_de: [
      "Die Evidenz-Einstufung gesichert, wahrscheinlich oder unzureichend ist eine redaktionelle Bewertung der Quellenlage, kein standardisiertes Evidenzniveau.",
      "Kombinationen ohne hinterlegte Quellen im Detail-Panel sind noch nicht gegen eine klinische Quelle geprüft.",
    ],
    openClaims_en: [
      "The evidence rating established, probable or insufficient is an editorial assessment of the source situation, not a standardised level of evidence.",
      "Combinations without sources filed in the detail panel have not yet been checked against a clinical source.",
    ],
  },
  "/harm-reduction": {
    intro_de:
      "Die Beiträge in diesem Bereich sind teils Erfahrungsberichte, teils datengestützte Aufklärung. Beides wird hier getrennt ausgewiesen.",
    intro_en:
      "The articles in this section are partly experience reports and partly data-driven information. The two are kept separate here.",
    sourceIds: ["euda-edr-2026", "awmf-medikamentenbezogene-stoerungen"],
    openClaims_de: [
      "Beiträge zu neuen psychoaktiven Substanzen wie 3-FPO, DNT-9, T9HC oder PHC beruhen auf Selbstbeobachtung und Produktbeobachtung. Zu diesen Stoffen existiert keine begutachtete Humanpharmakologie, die zitiert werden könnte. Das ist Teil der Aussage, nicht eine Lücke im Beleg.",
      "Produktbezogene Angaben ohne Analysezertifikat (COA) sind Herstellerangaben und nicht unabhängig geprüft.",
    ],
    openClaims_en: [
      "Articles on novel psychoactive substances such as 3-FPO, DNT-9, T9HC or PHC rest on self-observation and product observation. No peer-reviewed human pharmacology exists for these substances that could be cited. That is part of the point, not a gap in sourcing.",
      "Product claims without a certificate of analysis (COA) are manufacturer statements and are not independently verified.",
    ],
  },
  "/notfall": {
    intro_de:
      "Die Notfallschritte folgen der allgemeinen Erste-Hilfe-Praxis: Notruf 112, stabile Seitenlage, Ansprechen und Atmung kontrollieren, Naloxon bei Verdacht auf Opioid-Überdosis. Die Liste der kritischen Kombinationen stammt aus der Interaktions-Matrix und nutzt deren Quellenlage.",
    intro_en:
      "The emergency steps follow general first aid practice: call 112, recovery position, check responsiveness and breathing, naloxone when an opioid overdose is suspected. The list of critical combinations comes from the interaction matrix and shares its sourcing.",
    sourceIds: ["cdc-opioid-2022", "fda-benzo-boxed-warning", "euda-edr-2026"],
    openClaims_de: [
      "Naloxon-Verfügbarkeit und regionale Abgabestellen ändern sich laufend. Die aktuelle Situation ist nicht durch eine hier hinterlegte Quelle belegt.",
      "Die genauen Symptome je Substanzgruppe sind Kurzfassungen ohne eigene Einzelquelle.",
    ],
    openClaims_en: [
      "Naloxone availability and regional distribution points change constantly. The current situation is not backed by a source filed here.",
      "The exact symptom descriptions per substance class are summaries without their own individual source.",
    ],
  },
};
